import { router } from '@inertiajs/vue3';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';
import { destinationLayoutContext, resolveDestinationSkeleton } from '@/ShopManagement/destinationSkeleton.js';

export const useInertiaContentSkeleton = (page) => {
  const contentLoading = ref(false);
  const destinationSkeleton = shallowRef(null);
  const showContentSkeleton = computed(() => destinationSkeleton.value !== null);
  const visitKey = ref(0);
  const pageKey = computed(() => `${page.component}:${page.url}`);
  let activeVisit = null;
  let finished = false;
  let ready = null;
  let removeStartListener = null;
  let removeFinishListener = null;

  const isBackgroundVisit = (event) =>
    event?.detail?.visit?.showProgress === false ||
    event?.detail?.visit?.prefetch === true ||
    event?.detail?.visit?.async === true;

  const clear = () => {
    activeVisit = null;
    finished = false;
    contentLoading.value = false;
    destinationSkeleton.value = null;
  };
  const reveal = () => {
    if (finished && ready?.visitKey === visitKey.value && ready?.pageKey === pageKey.value) clear();
  };
  const presentationReady = (presentation) => {
    ready = presentation;
    reveal();
  };
  const start = (event) => {
    if (!event?.detail?.visit || isBackgroundVisit(event)) return;
    const visit = event.detail.visit;
    activeVisit = visit;
    finished = false;
    ready = null;
    visitKey.value++;
    contentLoading.value = true;
    const destination = new URL(String(visit.url), window.location.origin);
    const samePage = destination.pathname.replace(/\/+$/, '') === window.location.pathname.replace(/\/+$/, '');
    destinationSkeleton.value = String(visit.method).toLowerCase() === 'get' && !visit.only?.length && !samePage
      ? resolveDestinationSkeleton(destination, window.location.origin, destinationLayoutContext(page.props))
      : null;
  };
  const finish = async (event) => {
    if (isBackgroundVisit(event) || event?.detail?.visit !== activeVisit) return;
    const visit = activeVisit;
    if (visit.cancelled || visit.interrupted || !visit.completed) {
      clear();
      return;
    }
    await nextTick();
    if (visit !== activeVisit) return;
    finished = true;
    // A mounted page has announced its actual loading/content/error frame.
    // No timeout or animation-frame count is used as evidence of readiness.
    reveal();
  };
  onMounted(() => {
    removeStartListener = router.on('start', start);
    removeFinishListener = router.on('finish', finish);
  });
  onBeforeUnmount(() => {
    removeStartListener?.();
    removeFinishListener?.();
    clear();
  });
  return { contentLoading, destinationSkeleton, showContentSkeleton, visitKey, pageKey, presentationReady };
};
